import { Box, Container, Grid, Typography, Button, Stack } from '@mui/material';
import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Free',
    price: 'R0',
    period: '/month',
    description: 'Perfect for small businesses getting started',
    employeeRange: '1-10 employees',
    features: [
      'Employee Onboarding',
      'Basic Leave Management',
      'OHS Tracking',
      'Document Storage',
      'Email Support',
    ],
    cta: 'Get Started Free',
    featured: false,
  },
  {
    name: 'Professional',
    price: 'R149',
    period: '/month',
    description: 'For growing companies that need full HR capabilities',
    employeeRange: '11-100 employees',
    features: [
      'Everything in Free',
      'Skills Development (WSP/ATR)',
      'Industrial Relations',
      'Employment Equity',
      'Audit Trail',
      'Compliance Calendar',
      'Priority Support',
    ],
    cta: 'Start Professional',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organisations with complex requirements',
    employeeRange: '100+ employees',
    features: [
      'Everything in Professional',
      'Custom Integrations',
      'Dedicated Account Manager',
      'SLA Guarantee',
      'Training & Onboarding',
      'Custom Reports',
      'API Access',
    ],
    cta: 'Contact Sales',
    featured: false,
  },
];

export function PricingSection() {
  return (
    <Box id="pricing" sx={{ py: 12, bgcolor: '#0a0a18' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Typography sx={{ color: '#6b7a99', fontSize: 14, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
            Simple Pricing
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: 32, md: 48 },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#f0f0f5',
              mb: 2,
            }}
          >
            Plans That Scale With You
          </Typography>
          <Typography variant="h6" sx={{ color: '#a0a5b5', fontWeight: 400 }}>
            Start free, upgrade when you need more.
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {plans.map((plan) => (
            <Grid size={{ xs: 12, md: 4 }} key={plan.name}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: plan.featured
                    ? '2px solid rgba(61, 122, 255, 0.5)'
                    : '1px solid rgba(255,255,255,0.06)',
                  backgroundColor: plan.featured ? 'rgba(61, 122, 255, 0.05)' : 'rgba(18, 18, 31, 0.6)',
                  backdropFilter: 'blur(16px)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: plan.featured ? '0 20px 40px -12px rgba(61,122,255,0.3)' : '0 20px 40px -12px rgba(0,0,0,0.5)',
                  },
                }}
              >
                {plan.featured && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      px: 2,
                      py: 0.5,
                      borderRadius: '9999px',
                      background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Most Popular
                  </Box>
                )}

                <Typography variant="h6" sx={{ fontWeight: 600, color: '#f0f0f5', mb: 1 }}>
                  {plan.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a5b5', mb: 3 }}>
                  {plan.description}
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 48, fontWeight: 800, color: '#f0f0f5', lineHeight: 1 }}>
                    {plan.price}
                  </Typography>
                  {plan.period && (
                    <Typography sx={{ color: '#a0a5b5', fontSize: 16 }}>{plan.period}</Typography>
                  )}
                </Stack>
                <Typography variant="body2" sx={{ color: '#a0a5b5', mb: 3 }}>{plan.employeeRange}</Typography>

                <Box sx={{ flex: 1 }}>
                  {plan.features.map((feature) => (
                    <Stack key={feature} direction="row" spacing={1} sx={{ mb: 1.5 }}>
                      <Box sx={{ color: '#22c55e', fontSize: 14, fontWeight: 700 }}>✓</Box>
                      <Typography variant="body2" sx={{ color: '#a0a5b5' }}>{feature}</Typography>
                    </Stack>
                  ))}
                </Box>

                <Button
                  component={Link}
                  to="/signup"
                  variant={plan.featured ? 'contained' : 'outlined'}
                  fullWidth
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: '9999px',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    ...(plan.featured
                      ? {
                          background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                          '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)' },
                        }
                      : {
                          borderColor: 'rgba(255,255,255,0.15)',
                          color: '#a0a5b5',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.3)', color: '#f0f0f5' },
                        }),
                  }}
                >
                  {plan.cta}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
